import * as z from 'zod/mini'
import { endAfterStart, isoDateTime, messageField } from '../../schemas/common'
import { eventFields, reoccurrenceFields, reoccurrenceRule } from '../../schemas/event'
import { reservationTextFields } from '../../schemas/reservation'
import { SERIES_EDIT_FIELDS } from '../../utils/event-series-edit'
import { ReoccurType } from '../../types/enums/reoccur-type.enum'
import { WeekDay } from '../../types/enums/week-day.enum'
import type { BasicInfo } from '../../types/interfaces'

/** What every new event and event blueprint carries. Ids are resolved within the premise in the service (404). */
const eventBodyFields = {
  name: eventFields.name,
  activityId: z.uuid(),
  placeId: z.uuid(),
  capacity: eventFields.capacity.check(z.positive()),
  employeeIds: z.array(z.uuid()),
  visibility: z.optional(eventFields.visibility),
  price: z.optional(eventFields.price.check(z.positive())),
  /** Omitted, the event shows its activity's colour - and follows it if the activity is recoloured. */
  color: z.optional(eventFields.color),
  /** HTML shown to customers on the sign-up step; sanitised by the API. A blueprint copies it to every event. */
  description: z.optional(eventFields.description),
  /** `false` creates the event locked: visible, but closed to sign-up until unlocked. Omitted means open. */
  active: z.optional(z.boolean()),
  /** Until when a participant may cancel through their link. Omitted means until the start (`0`). */
  cancellationDeadline: z.optional(eventFields.cancellationDeadline),
  /** The public sign-up offers a note for the premise. Omitted means off; a blueprint copies it to every event. */
  allowCustomerNote: z.optional(eventFields.allowCustomerNote),
}

/**
 * A one-off event. An all-day event is placed by `date` (the service derives start and end as the
 * premise's day); a timed one by `start` and `end`, and must end after it starts. `start`/`end` sent
 * with an all-day event are ignored.
 */
export const createEventBody = z.discriminatedUnion('allDay', [
  z.object({
    ...eventBodyFields,
    allDay: z.literal(true),
    date: isoDateTime,
    start: z.optional(isoDateTime),
    end: z.optional(isoDateTime),
  }),
  z
    .object({
      ...eventBodyFields,
      allDay: z.literal(false),
      date: z.optional(isoDateTime),
      start: isoDateTime,
      end: isoDateTime,
    })
    .check(z.superRefine(endAfterStart('start', 'end'))),
])
export type CreateEventBody = z.infer<typeof createEventBody>

/**
 * A repeating event. The blueprint stores the time of day (`start`/`end`, required unless all-day)
 * and the recurrence; the dates come from `reoccurFrom` and the recurrence rule. `createEvents`
 * generates the events right away.
 */
const eventBlueprintBodyFields = {
  ...eventBodyFields,
  /** "Názov série": the series' own name for managers, never sent to clients and never edited afterwards. */
  seriesName: eventFields.seriesName,
  ...reoccurrenceFields,
  reoccurFrom: isoDateTime,
  reoccurUntil: z.optional(isoDateTime),
  createEvents: z.optional(z.boolean()),
}

export const createEventBlueprintBody = z
  .discriminatedUnion('allDay', [
    z.object({
      ...eventBlueprintBodyFields,
      allDay: z.literal(true),
      start: z.optional(isoDateTime),
      end: z.optional(isoDateTime),
    }),
    z
      .object({ ...eventBlueprintBodyFields, allDay: z.literal(false), start: isoDateTime, end: isoDateTime })
      .check(z.superRefine(endAfterStart('start', 'end'))),
  ])
  .check(z.superRefine(reoccurrenceRule))
export type CreateEventBlueprintBody = z.infer<typeof createEventBlueprintBody>

/** What an edit may change besides the time and the activity - the fields a series edit carries too. */
const editEventFields = {
  name: z.optional(eventFields.name),
  placeId: z.optional(z.uuid()),
  capacity: z.optional(eventFields.capacity.check(z.positive())),
  employeeIds: z.optional(z.array(z.uuid())),
  visibility: z.optional(eventFields.visibility),
  /** `null` removes the price. */
  price: z.optional(z.nullable(eventFields.price.check(z.positive()))),
  /** `null` returns the event to its activity's colour. */
  color: z.optional(z.nullable(eventFields.color)),
  /** `''` removes it. A single edit changes this event only; a series edit, the termíny it reaches. */
  description: z.optional(eventFields.description),
  /** Applies to sign-ups from now on; each existing one keeps the deadline it was made with. */
  cancellationDeadline: z.optional(eventFields.cancellationDeadline),
  /** Applies to sign-ups from now on; a note already written stays. */
  allowCustomerNote: z.optional(eventFields.allowCustomerNote),
}

/**
 * "Upozorniť účastníkov o zmene e-mailom" (docs/events.md, _Telling participants_): off unless asked. Each live
 * participant of a changed termín whose name, place, employees or price changed gets one e-mail per address (E19), with
 * the manager's `message` when there is one.
 */
const notifyFields = {
  notifyCustomers: z.optional(z.boolean()),
  message: z.optional(messageField),
}

/**
 * A partial edit. Whether the result is a valid event depends on what is stored, so
 * `EventService.editEvent` checks the merged values; the schema only checks the pair when both are sent.
 */
export const editEventBody = z
  .object({
    ...editEventFields,
    activityId: z.optional(z.uuid()),
    date: z.optional(isoDateTime),
    start: z.optional(isoDateTime),
    end: z.optional(isoDateTime),
    allDay: z.optional(z.boolean()),
    ...notifyFields,
  })
  .check(z.superRefine(endAfterStart('start', 'end')))
export type EditEventBody = z.infer<typeof editEventBody>

/**
 * "Tento a nasledujúce": the opened termín and every later upcoming one of its series (docs/events.md, _Editing a
 * series_). `changes` holds the fields in play: the ones the manager changed, and the unified ones ("Zjednotiť": in
 * `overwrite`, with the opened termín's own value). A termín already holding the new value is not written; a later one
 * holding its own value - neither the opened termín's nor the new - keeps it, unless the field is in `overwrite`.
 */
export const editEventSeriesBody = z.object({
  changes: z.object(editEventFields),
  overwrite: z.array(z.enum(SERIES_EDIT_FIELDS)),
  ...notifyFields,
})
export type EditEventSeriesBody = z.infer<typeof editEventSeriesBody>

export interface EditEventSeriesResponse {
  /** Termíny written, the opened one included. */
  updated: number
  /** Later termíny that kept their own value in at least one changed field. */
  kept: number
  /** Addresses e-mailed about the change; `0` when the manager did not ask, or nobody needed telling. */
  notified: number
}

/**
 * "Pridať do série": a one-time termín joins this series of the premise (docs/events.md, _Adding an event to a series_).
 * Only the link is written; refused unless the series is active and has the event's activity.
 */
export const joinEventSeriesBody = z.object({ blueprintId: z.uuid() })
export type JoinEventSeriesBody = z.infer<typeof joinEventSeriesBody>

/** A series the termín may join: active, with the event's activity. What the dialog's option line needs. */
export interface SeriesOption {
  id: string
  /** The series name, or its termíny' name for a series created before it had one (docs/events.md, _Series name_). */
  name: string
  /** The series' time of day: the first termín's `start`/`end`, read in the premise's timezone. */
  start: Date | null
  end: Date | null
  allDay: boolean
  /** How it repeats; `null` when the series has no recurrence stored. */
  reoccurType: ReoccurType | null
  weekdays: WeekDay[]
  place: BasicInfo | null
  /** The start of its nearest upcoming, not cancelled termín. */
  next: Date
  /** How many upcoming, not cancelled termíny it has; at least 1, or it is done and not offered. */
  upcoming: number
  /** The start of its last upcoming termín. */
  last: Date
}

export const setEventActiveBody = z.object({ active: z.boolean() })
export type SetEventActiveBody = z.infer<typeof setEventActiveBody>

/**
 * The premise calls the event off: it stays, marked cancelled, and every live sign-up is cancelled with it. Refused
 * once the event started (`event.started`) or if already cancelled (`event.cancelled`); docs/events.md, _Cancelling
 * an event_.
 */
export const cancelEventBody = z.object({
  /** Each participant with an address gets E18. */
  notifyCustomers: z.boolean(),
  /** Written for the participants: goes into E18 and onto each cancelled sign-up. */
  reason: z.optional(reservationTextFields.cancellationReason),
})
export type CancelEventBody = z.infer<typeof cancelEventBody>

/**
 * A premise's events in a window. Both bounds are instants (`toISOString`). `blueprintId` narrows
 * the list to one series - the detail page's Séria strip.
 */
export const getManagedEventsQuery = z.object({
  start: z.optional(isoDateTime),
  end: z.optional(isoDateTime),
  blueprintId: z.optional(z.uuid()),
})
export type GetManagedEventsQuery = z.infer<typeof getManagedEventsQuery>

/** An event URL (`.../events/:eventId`), looked up by `EventGuard` within the premise. */
export const eventParams = z.object({ eventId: z.uuid() })
export type EventParams = z.infer<typeof eventParams>
